import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PaperAirplaneIcon, XMarkIcon, MegaphoneIcon, BellAlertIcon, CheckBadgeIcon,
  ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon, DocumentArrowDownIcon, UserPlusIcon, PencilSquareIcon, ArrowPathIcon,
  ClockIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { msalInstance } from '../auth/msalInstance';
import { useComms, useComm, useCommTargets, useCreateComm, useUpdateComm, useRemindComm, useAddRecipients, useAddNewMembers, useNewMembers, useReleaseComm, useCancelComm, fetchCommAnalyticsPdf } from '../hooks/useComms';
import MessageBuilder, { blankBlock, blockHasContent, uploadedMediaIds } from './MessageBuilder';
import MessageBlocks from './MessageBlocks';
import DocumentViewer from './DocumentViewer';
import { COMMS_DEPARTMENTS, GENERAL, deptLabel } from './commsDepartments';

const PAGE_SIZE = 25;

export function DeptBadge({ department }) {
  return <span className="rounded bg-brand-light px-1.5 py-0.5 text-xs font-medium text-brand">{deptLabel(department)}</span>;
}

// The header a message is filed under in everyone's inbox. Required when
// composing; `allowNone` lets an older, unfiled message be edited without
// forcing a choice.
function DepartmentSelect({ value, onChange, allowNone }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-500">Department header</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-md border px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${value ? 'border-gray-300 text-gray-900' : 'border-gray-300 text-gray-400'}`}>
        <option value="">{allowNone ? 'General (no header)' : 'Choose a department…'}</option>
        {COMMS_DEPARTMENTS.filter((d) => !d.system).map((d) => <option key={d.key} value={d.key} className="text-gray-900">{d.label}</option>)}
      </select>
      <p className="mt-1 text-[11px] text-gray-400">Recipients' inboxes are split by this header.</p>
    </div>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
const creatorText = (c) => `${c.sender_name || '—'}${c.sender_email ? ` (${c.sender_email})` : ''}`;

// ---- Release time + deadline ------------------------------------------------
// <input type="datetime-local"> has no time zone: it shows and returns the
// author's own local time. It is turned into an instant here, before it goes
// to the server.
const pad2 = (n) => String(n).padStart(2, '0');
const toLocalInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}T${pad2(x.getHours())}:${pad2(x.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const fmtWhen = (d) => (d ? new Date(d).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const STAGE_LABEL = { '3d': '3 days before', '1d': '1 day before', '2h': '2 hours before' };

// `canSchedule` is false when editing a message that has already gone out.
function TimingFields({ canSchedule, alreadyScheduled, when, setWhen, scheduleAt, setScheduleAt, dueAt, setDueAt, autoRemind, setAutoRemind, requiresAck }) {
  const inputCls = 'rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
  const minNow = toLocalInput(new Date());
  return (
    <div className="space-y-3 rounded-md border border-gray-200 p-3">
      {canSchedule && (
        <div>
          <p className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-500"><ClockIcon className="h-3.5 w-3.5" /> When it goes out</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-700">
            {!alreadyScheduled && (
              <label className="flex items-center gap-1.5"><input type="radio" checked={when === 'now'} onChange={() => setWhen('now')} /> Send now</label>
            )}
            <label className="flex items-center gap-1.5">
              {!alreadyScheduled && <input type="radio" checked={when === 'later'} onChange={() => setWhen('later')} />}
              {alreadyScheduled ? 'Scheduled for' : 'Schedule for'}
            </label>
            {(when === 'later' || alreadyScheduled) && (
              <input type="datetime-local" value={scheduleAt} min={minNow} onChange={(e) => setScheduleAt(e.target.value)} className={inputCls} />
            )}
          </div>
          {(when === 'later' || alreadyScheduled) && (
            <p className="mt-1 text-[11px] text-gray-400">Nobody sees it until then. Group, region and department audiences are worked out at that moment, so anyone who joins before it goes out is included.</p>
          )}
        </div>
      )}
      <div>
        <p className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-500"><CalendarDaysIcon className="h-3.5 w-3.5" /> Complete by <span className="font-normal text-gray-400">(optional)</span></p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="datetime-local" value={dueAt} min={when === 'later' && scheduleAt ? scheduleAt : minNow} onChange={(e) => setDueAt(e.target.value)} className={inputCls} />
          {dueAt && <button type="button" onClick={() => setDueAt('')} className="text-xs text-gray-400 hover:text-gray-600">Remove deadline</button>}
        </div>
        {dueAt && (
          <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={autoRemind} onChange={(e) => setAutoRemind(e.target.checked)} className="mt-0.5" />
            <span>Automatically remind anyone who hasn't {requiresAck ? 'acknowledged' : 'read'} it — 3 days, 1 day and 2 hours before the deadline (push and email).</span>
          </label>
        )}
      </div>
    </div>
  );
}

export function Bar({ value, total, tone = 'brand' }) {
  const p = pct(value, total);
  const bg = tone === 'green' ? 'bg-emerald-500' : 'bg-brand';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${bg}`} style={{ width: `${p}%` }} />
      </div>
      <span className="tabular-nums text-xs text-gray-600">{value}/{total} ({p}%)</span>
    </div>
  );
}


// ---- Live preview -------------------------------------------------------------
// The message as it will appear to someone opening it, updated as it is written.
// Framed as a phone because most recipients read comms in the FSM app, and laid
// out like that app's message screen. The body goes through MessageBlocks -- the
// component both apps render with -- so it cannot drift from what is delivered.
// Wide screens only: below lg the builder's Preview button does the same job.
function LivePreview({ department, subject, blocks, priority, requiresAck, dueAt, scheduleAt, sentAt, senderName }) {
  const filled = blocks.filter(blockHasContent);
  const empty = !subject.trim() && !filled.length;
  const [viewingDoc, setViewingDoc] = useState(null);
  return (
    <div className="hidden lg:sticky lg:top-0 lg:block">
      {viewingDoc && <DocumentViewer block={viewingDoc} onClose={() => setViewingDoc(null)} />}
      <p className="mb-2 text-center text-xs font-medium text-gray-500">Live preview <span className="font-normal text-gray-400">— what recipients see</span></p>
      <div className="mx-auto w-full max-w-[360px] rounded-[2rem] border-[10px] border-gray-800 bg-gray-50 shadow-lg">
        <div className="mx-auto mt-1.5 h-1.5 w-16 rounded-full bg-gray-700" />
        <div className="max-h-[68vh] overflow-y-auto p-3">
          {empty ? (
            <p className="px-2 py-16 text-center text-sm text-gray-400">Start writing and the message appears here.</p>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <p className={`mb-1 text-xs font-semibold uppercase tracking-wide ${department ? 'text-brand' : 'text-gray-300'}`}>
                {department ? deptLabel(department) : 'No department chosen'}
              </p>
              <h1 className="text-lg font-bold text-gray-900">
                {priority === 'high' && <span className="mr-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600">High</span>}
                {subject.trim() || <span className="text-gray-300">Subject</span>}
              </h1>
              <p className="mt-1 text-xs text-gray-400">From {senderName || 'you'} · {sentAt ? fmtDate(sentAt) : scheduleAt ? fmtDate(fromLocalInput(scheduleAt)) : 'now'}</p>
              {dueAt && <p className="mt-1 text-sm font-medium text-gray-600">Complete by {fmtDate(fromLocalInput(dueAt))}</p>}
              {requiresAck && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                  This message requires your acknowledgement after reading it.
                </div>
              )}
              {/* Links and documents open in a new tab from here, never in place of the composer. */}
              <div
                className="mt-4"
                role="presentation"
                onClickCapture={(e) => {
                  const a = e.target.closest && e.target.closest('a[href]');
                  if (!a || a.target === '_blank') return;
                  e.preventDefault();
                  window.open(a.href, '_blank', 'noopener,noreferrer');
                }}
              >
                {filled.length ? <MessageBlocks blocks={filled} onOpenDocument={setViewingDoc} /> : <p className="text-sm text-gray-300">Message content</p>}
              </div>
              {requiresAck && (
                <div className="mt-6 border-t border-gray-100 pt-4">
                  <div className="w-full rounded-lg bg-brand px-4 py-3 text-center text-base font-semibold text-white opacity-60">I acknowledge I have read this</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Drafts -----------------------------------------------------------------
// Unsent work is kept in this browser until it is sent or thrown away. A
// message can be lost without the author closing anything: the sign-in
// renewal redirects the whole page, and a link followed from the page takes
// the composer with it. Storage can be full (images are held inline) or
// blocked, so every call is guarded and the composer works without it.
const draftKey = (what) => `neo2.comms.draft.${msalInstance.getActiveAccount()?.localAccountId || 'anon'}.${what}`;
function readDraft(key) { try { const s = window.localStorage.getItem(key); return s ? JSON.parse(s) : null; } catch { return null; } }
function writeDraft(key, value) { try { window.localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } }
function clearDraft(key) { try { window.localStorage.removeItem(key); } catch { /* storage unavailable */ } }

// Saves `value` a moment after it stops changing; null clears the draft.
// Returns false when the browser refused to store it.
function useDraftAutosave(key, value, active) {
  const [ok, setOk] = useState(true);
  const json = JSON.stringify(value);
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => {
      if (value === null) { clearDraft(key); setOk(true); } else setOk(writeDraft(key, { ...value, savedAt: new Date().toISOString() }));
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, json, active]);
  return ok;
}

function DraftNotice({ savedAt, onDiscard, saveFailed }) {
  return (
    <>
      {savedAt && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-brand/30 bg-brand-light/40 px-3 py-2 text-xs text-gray-700 lg:col-span-2">
          <span>Picked up your unsent changes from {fmtDate(savedAt)}.</span>
          <button type="button" onClick={onDiscard} className="font-medium text-brand hover:text-brand-dark">Discard them and start again</button>
        </div>
      )}
      {saveFailed && (
        <p className="text-xs text-amber-700 lg:col-span-2">This message is too large to keep as a draft in this browser — send it before leaving the page.</p>
      )}
    </>
  );
}

// ---- Composer ---------------------------------------------------------------
function Composer({ onClose }) {
  const { data: opts } = useCommTargets();
  const create = useCreateComm();
  const key = draftKey('new');
  const [draft, setDraft] = useState(() => readDraft(key));
  const [department, setDepartment] = useState(draft?.department || '');
  const [subject, setSubject] = useState(draft?.subject || '');
  const [blocks, setBlocks] = useState(() => (Array.isArray(draft?.blocks) && draft.blocks.length ? draft.blocks : [blankBlock('text')]));
  const [priority, setPriority] = useState(draft?.priority || 'normal');
  const [requiresAck, setRequiresAck] = useState(!!draft?.requiresAck);
  const [sendEmail, setSendEmail] = useState(!!draft?.sendEmail);
  const [groupId, setGroupId] = useState(draft?.groupId || ''); // optional Entra security group
  const [when, setWhen] = useState(draft?.when === 'later' ? 'later' : 'now');
  const [scheduleAt, setScheduleAt] = useState(draft?.scheduleAt || '');
  const [dueAt, setDueAt] = useState(draft?.dueAt || '');
  const [autoRemind, setAutoRemind] = useState(draft?.autoRemind !== false);
  const [filter, setFilter] = useState('all'); // 'all' | 'engineers' | 'dept:X' | 'region:Y' | 'isp:Z' | 'cluster:W'
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(() => new Set(draft?.selected || []));
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const people = useMemo(() => opts?.people || [], [opts]);
  const me = msalInstance.getActiveAccount()?.localAccountId;

  // Only worth keeping once something has been written; recipients alone are
  // not a draft (the author is pre-selected on every new message).
  const hasWork = !!department || !!subject.trim() || blocks.some(blockHasContent);
  const draftSaved = useDraftAutosave(
    key,
    hasWork ? { department, subject, blocks, priority, requiresAck, sendEmail, groupId, selected: [...selected], when, scheduleAt, dueAt, autoRemind } : null,
    !result,
  );
  const discardDraft = () => {
    clearDraft(key); setDraft(null);
    setDepartment(''); setSubject(''); setBlocks([blankBlock('text')]); setPriority('normal');
    setRequiresAck(false); setSendEmail(false); setGroupId('');
    setWhen('now'); setScheduleAt(''); setDueAt(''); setAutoRemind(true);
    setSelected(new Set(me && people.some((p) => p.entraId === me) ? [me] : []));
  };

  // Auto-select the creator once the people list has loaded -- unless a draft
  // was restored, whose recipient list is the author's own choice.
  const [creatorSeeded, setCreatorSeeded] = useState(() => !!draft);
  useEffect(() => {
    if (creatorSeeded || !people.length || !me) return;
    if (people.some((p) => p.entraId === me)) setSelected((s) => new Set(s).add(me));
    setCreatorSeeded(true);
  }, [people, me, creatorSeeded]);

  const isEngineer = (p) => p.department && !/^office/i.test(p.department) && p.department !== 'Leaver';
  const matchesFilter = (p) => {
    if (filter === 'all') return true;
    if (filter === 'engineers') return isEngineer(p);
    const [t, v] = filter.split(/:(.+)/);
    if (t === 'dept') return p.department === v;
    if (t === 'region') return p.region === v;
    if (t === 'isp') return p.isp === v;
    if (t === 'cluster') return p.cluster === v;
    return true;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter((p) => matchesFilter(p)
      && (!q || (p.name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, filter, search]);

  useEffect(() => { setPage(0); }, [filter, search]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const departments = useMemo(() => [...new Set(people.map((p) => p.department).filter(Boolean))].sort(), [people]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAllMatching = () => setSelected((s) => { const n = new Set(s); filtered.forEach((p) => n.add(p.entraId)); return n; });
  const clearMatching = () => setSelected((s) => { const n = new Set(s); filtered.forEach((p) => n.delete(p.entraId)); return n; });

  const submit = async () => {
    setError('');
    if (!department) return setError('Choose a department header.');
    if (!subject.trim()) return setError('Add a subject.');
    const filled = blocks.filter(blockHasContent);
    if (!filled.length) return setError('Add some message content.');
    const targets = [];
    if (selected.size) targets.push({ mode: 'individuals', entraIds: [...selected] });
    if (groupId) targets.push({ mode: 'group', groupId });
    if (!targets.length) return setError('Select at least one recipient.');
    if (when === 'later' && !scheduleAt) return setError('Choose when the message should go out.');
    if (when === 'later' && new Date(scheduleAt).getTime() < Date.now() + 60 * 1000) return setError('Choose a release time in the future, or send it now.');
    if (dueAt && new Date(dueAt).getTime() <= (when === 'later' && scheduleAt ? new Date(scheduleAt).getTime() : Date.now())) {
      return setError(when === 'later' ? 'The deadline must be after the release time.' : 'The deadline must be in the future.');
    }
    try {
      const res = await create.mutateAsync({
        department,
        subject,
        body: JSON.stringify(filled),
        format: 'blocks',
        media_ids: uploadedMediaIds(filled),
        priority, requires_ack: requiresAck, send_email: sendEmail, targets,
        scheduled_for: when === 'later' ? fromLocalInput(scheduleAt) : null,
        due_at: fromLocalInput(dueAt),
        auto_remind: autoRemind,
        // Author gets a one-off email linking the ISO briefing register form.
        register_prompt: true,
      });
      clearDraft(key);
      setResult(res);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not send the message.');
    }
  };

  if (result) {
    if (result.scheduled) {
      return (
        <Modal onClose={onClose} title="Message scheduled">
          <div className="text-center">
            <ClockIcon className="mx-auto mb-3 h-10 w-10 text-brand" />
            <p className="text-sm text-gray-700">It will go out <span className="font-semibold">{fmtWhen(result.scheduled_for)}</span>.</p>
            <p className="mt-1 text-xs text-gray-400">About {result.recipient_count} recipient{result.recipient_count === 1 ? '' : 's'} today · {result.audience_summary}</p>
            <p className="mt-2 text-xs text-gray-400">You can change the time, send it early or cancel it from Comms Dispatch until then.</p>
            <button type="button" onClick={onClose} className="mt-5 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark">Done</button>
          </div>
        </Modal>
      );
    }
    return (
      <Modal onClose={onClose} title="Message sent">
        <div className="text-center">
          <PaperAirplaneIcon className="mx-auto mb-3 h-10 w-10 text-brand" />
          <p className="text-sm text-gray-700">Sent to <span className="font-semibold">{result.recipient_count}</span> recipient{result.recipient_count === 1 ? '' : 's'}.</p>
          <p className="mt-1 text-xs text-gray-400">{result.audience_summary}</p>
          <button type="button" onClick={onClose} className="mt-5 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark">Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title="New message" full>
      {/* Compose on the left, live preview on the right; recipients full width below. */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <DraftNotice savedAt={draft?.savedAt} onDiscard={discardDraft} saveFailed={!draftSaved} />
        <div className="space-y-4">
        <DepartmentSelect value={department} onChange={setDepartment} />
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={255}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
        </div>
        <MessageBuilder blocks={blocks} onChange={setBlocks} livePreview />

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-xs font-medium text-gray-500">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-sm">
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} /> Require acknowledgement
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} /> Send email notification <span className="text-gray-400">(link only — no message content, they must open Nano)</span>
          </label>
        </div>

        <TimingFields
          canSchedule
          when={when} setWhen={setWhen} scheduleAt={scheduleAt} setScheduleAt={setScheduleAt}
          dueAt={dueAt} setDueAt={setDueAt} autoRemind={autoRemind} setAutoRemind={setAutoRemind}
          requiresAck={requiresAck}
        />

        </div>{/* end left (compose) column */}

        <LivePreview
          department={department} subject={subject} blocks={blocks} priority={priority} requiresAck={requiresAck}
          dueAt={dueAt} scheduleAt={when === 'later' ? scheduleAt : ''}
          senderName={msalInstance.getActiveAccount()?.name}
        />

        <div className="rounded-lg border border-gray-200 p-3 lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Recipients</p>
            <span className="rounded-full bg-brand-light px-2 py-0.5 text-xs font-medium text-brand">{selected.size} selected</span>
          </div>

          {/* Group filter + search */}
          <div className="mb-2 flex flex-wrap gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
              <option value="all">All staff ({people.length})</option>
              <option value="engineers">Engineers</option>
              {departments.length > 0 && (
                <optgroup label="Department">{departments.map((d) => <option key={d} value={`dept:${d}`}>{d}</option>)}</optgroup>
              )}
              {opts?.regions?.length > 0 && (
                <optgroup label="Region">{opts.regions.map((r) => <option key={r} value={`region:${r}`}>{r}</option>)}</optgroup>
              )}
              {opts?.isps?.length > 0 && (
                <optgroup label="ISP">{opts.isps.map((r) => <option key={r} value={`isp:${r}`}>{r}</option>)}</optgroup>
              )}
              {opts?.clusters?.length > 0 && (
                <optgroup label="Cluster">{opts.clusters.map((r) => <option key={r} value={`cluster:${r}`}>{r}</option>)}</optgroup>
              )}
            </select>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…"
              className="min-w-[160px] flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
          </div>

          <div className="mb-2 flex items-center gap-3 text-xs">
            <button type="button" onClick={selectAllMatching} className="font-medium text-brand hover:text-brand-dark">Select all ({filtered.length})</button>
            <button type="button" onClick={clearMatching} className="text-gray-400 hover:text-gray-600">Clear these</button>
            <span className="ml-auto text-gray-400">{filtered.length} match{filtered.length === 1 ? '' : 'es'}</span>
          </div>

          {/* Paginated people table */}
          <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {paged.map((p) => {
                  const on = selected.has(p.entraId);
                  return (
                    <tr key={p.entraId} className={`cursor-pointer ${on ? 'bg-brand-light/40' : 'hover:bg-gray-50'}`} onClick={() => toggle(p.entraId)}>
                      <td className="w-8 px-2 py-1.5"><input type="checkbox" checked={on} readOnly /></td>
                      <td className="px-2 py-1.5 text-gray-700">{p.name}{p.entraId === me && <span className="ml-1 text-xs font-medium text-brand">(you)</span>}</td>
                      {/* Full width now, so there's room to tell two people with one name apart. */}
                      <td className="hidden px-2 py-1.5 text-xs text-gray-500 md:table-cell">{p.email || ''}</td>
                      <td className="px-2 py-1.5 text-right text-xs text-gray-400">{p.department || p.region || ''}</td>
                    </tr>
                  );
                })}
                {paged.length === 0 && <tr><td colSpan={4} className="px-2 py-4 text-center text-sm text-gray-400">No matches.</td></tr>}
              </tbody>
            </table>
          </div>

          {filtered.length > PAGE_SIZE && (
            <div className="mt-2 flex items-center justify-between text-xs">
              <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
                className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 disabled:opacity-40"><ChevronLeftIcon className="h-4 w-4" /> Prev</button>
              <span className="text-gray-500">Page {page + 1} of {pageCount}</span>
              <button type="button" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Next <ChevronRightIcon className="h-4 w-4" /></button>
            </div>
          )}

          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-500">Also include an Entra group (optional)</label>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm">
              <option value="">— none —</option>
              {(opts?.groups || []).map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
          </div>
        </div>

        {error && <p className="text-sm text-red-600 lg:col-span-2">{error}</p>}
        <div className="flex flex-wrap items-center justify-end gap-2 lg:col-span-2">
          {/* Who it goes to, beside the button that sends it -- the picker is now below the fold. */}
          <span className="mr-auto text-sm text-gray-600">
            {selected.size || groupId
              ? <>To <span className="font-semibold">{selected.size}</span> {selected.size === 1 ? 'person' : 'people'}{groupId ? <> + the <span className="font-semibold">{(opts?.groups || []).find((g) => g.id === groupId)?.label || 'selected'}</span> group</> : ''}</>
              : <span className="text-amber-700">No recipients selected yet</span>}
          </span>
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={submit} disabled={create.isPending}
            className="flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50">
            {when === 'later'
              ? <><ClockIcon className="h-4 w-4" /> {create.isPending ? 'Scheduling…' : 'Schedule message'}</>
              : <><PaperAirplaneIcon className="h-4 w-4" /> {create.isPending ? 'Sending…' : 'Send message'}</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Message analytics dashboard (mirrors training Course Insights) --------
function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center">
      <p className="text-2xl font-bold tabular-nums text-gray-900">{value}</p>
      <p className="text-xs text-gray-400">{label}</p>
    </div>
  );
}

// Stroke-based donut using the circumference=100 trick (r=15.9155). Wrapper
// rotated -90deg so segments start at 12 o'clock. Same as Course Insights.
function DonutChart({ segments, total, centreLabel = 'Recipients' }) {
  const RADIUS = 15.9155;
  let cumulative = 0;
  return (
    <div className="relative h-32 w-32 shrink-0">
      <svg viewBox="0 0 36 36" className="h-32 w-32 -rotate-90">
        <circle cx="18" cy="18" r={RADIUS} fill="none" stroke="#f3f4f6" strokeWidth="4" />
        {total > 0 && segments.filter((s) => s.value > 0).map((s) => {
          const p = (s.value / total) * 100;
          const offset = -cumulative;
          cumulative += p;
          return <circle key={s.key} cx="18" cy="18" r={RADIUS} fill="none" stroke={s.stroke} strokeWidth="4" strokeDasharray={`${p} ${100 - p}`} strokeDashoffset={offset} />;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-2xl font-bold text-gray-900">{total}</p>
        <p className="text-xs text-gray-400">{centreLabel}</p>
      </div>
    </div>
  );
}

function DonutLegend({ segments, total }) {
  return (
    <div className="flex flex-col gap-1.5">
      {segments.map((s) => (
        <div key={s.key} className="flex items-center gap-2 text-xs">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.stroke }} />
          <span className="text-gray-600">{s.label}</span>
          <span className="ml-auto font-medium text-gray-900">{s.value}</span>
          <span className="w-9 text-right text-gray-400">{total === 0 ? '—' : `${Math.round((s.value / total) * 100)}%`}</span>
        </div>
      ))}
    </div>
  );
}

// Green = done, red = not — the "lights" per state.
function StatusDot({ ok, title }) {
  return <span title={title} className={`inline-block h-3 w-3 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-400'}`} aria-hidden="true" />;
}

// Three states, not two. Red must mean "we tried and it failed" — never "we
// never tried". A message can be sent with email switched off, and a recipient
// with no device token has no push either; those people have had no outbound
// notification attempted at all, and showing them red reads as a broken send.
function NotifyDot({ state, title }) {
  if (state === 'none') return <span title={title} className="text-gray-300" aria-hidden="true">&mdash;</span>;
  return <span title={title} className={`inline-block h-3 w-3 rounded-full ${state === 'ok' ? 'bg-emerald-500' : 'bg-red-400'}`} aria-hidden="true" />;
}

// tracked=false means the message predates notified_at, so nothing can be said
// about it either way — dash, not a red dot that implies a failure.
function notifyState(r, sendEmail, tracked) {
  if (r.notified_at) return 'ok';
  if (!tracked) return 'none';
  return sendEmail && r.email ? 'fail' : 'none';
}
function notifyTitle(state) {
  if (state === 'ok') return 'Notified (email or push accepted)';
  if (state === 'fail') return 'Notification failed';
  return 'No notification attempted for this message';
}

const csvCell = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
function exportAnalyticsCsv(comm) {
  const header = ['Recipient', 'Email', 'Notified', 'Seen inbox', 'Read', 'Acknowledged', 'Watched %', 'Acked before watching', 'Acked, still not watched', 'Reminders'];
  const rows = comm.recipients.map((r) => [r.display_name || '', r.email || '', fmtDate(r.notified_at), fmtDate(r.delivered_at), fmtDate(r.read_at), fmtDate(r.acknowledged_at), r.watched_pct == null ? '' : r.watched_pct, r.acked_before_watching ? 'YES' : '', r.acked_unwatched ? 'YES' : '', r.reminders_sent || 0]);
  const csv = [['Message', comm.subject], ['Department', deptLabel(comm.department)], ['Created by', creatorText(comm)], ['Sent', fmtDate(comm.created_at)],
    ...(comm.edited_at ? [['Last edited', `${fmtDate(comm.edited_at)}${comm.edited_by ? ` by ${comm.edited_by}` : ''}`]] : []),
    ['Audience', comm.audience_summary || ''], [], header, ...rows]
    .map((row) => row.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = `message-analytics-${comm.id}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

const PAGE = 25;

// Read/acknowledged filters for the recipient table. Ack filters only make
// sense on a message that asked for one, so they are added conditionally.
const RECIP_FILTERS = (requiresAck, hasVideo) => [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
  ...(requiresAck ? [
    { key: 'acked', label: 'Acknowledged' },
    { key: 'notacked', label: 'Not acknowledged' },
  ] : []),
  ...(hasVideo ? [{ key: 'notwatched', label: 'Not watched' }] : []),
  ...(hasVideo && requiresAck ? [
    { key: 'ackedunwatched', label: 'Acked, still not watched' },
    { key: 'ackedblind', label: 'Acked before watching' },
  ] : []),
];
function matchesRecipFilter(r, filter) {
  if (filter === 'notwatched') return !r.all_watched;
  if (filter === 'ackedunwatched') return !!r.acked_unwatched;
  if (filter === 'ackedblind') return !!r.acked_before_watching;
  if (filter === 'read') return !!r.read_at;
  if (filter === 'unread') return !r.read_at;
  if (filter === 'acked') return !!r.acknowledged_at;
  if (filter === 'notacked') return !r.acknowledged_at;
  return true;
}

export function Detail({ id, onBack }) {
  const { data, isLoading, refetch, isFetching } = useComm(id);
  const remind = useRemindComm();
  const addNew = useAddNewMembers();
  const { data: newMembers } = useNewMembers(id);
  const release = useReleaseComm();
  const cancel = useCancelComm();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(() => new Set()); // comm_recipients.id to remind
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const toggleRecip = (rid) => setSelected((s) => { const n = new Set(s); if (n.has(rid)) n.delete(rid); else n.add(rid); return n; });

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { filename, base64 } = await fetchCommAnalyticsPdf(id);
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = filename || `message-analytics-${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } finally { setPdfBusy(false); }
  };

  // Older rows and automated messages carry no status column value of their
  // own; the server derives it, and anything unrecognised is treated as sent.
  const status = data?.status || 'sent';
  const isSent = status === 'sent';
  const recips = data?.recipients || [];
  const total = recips.length;
  const delivered = recips.filter((r) => r.delivered_at).length;
  const notified = recips.filter((r) => r.notified_at).length;
  // If nothing on this message carries a stamp it predates the tracking, so the
  // per-row dots show a dash rather than claiming every send failed.
  const tracked = notified > 0;
  const sendEmail = !!data?.send_email;
  const read = recips.filter((r) => r.read_at).length;
  const ack = recips.filter((r) => r.acknowledged_at).length;
  const reminders = recips.reduce((s, r) => s + (r.reminders_sent || 0), 0);
  const hasVideo = (data?.required_videos || []).length > 0;
  const ackedUnwatched = recips.filter((r) => r.acked_unwatched).length;
  const shown = recips.filter((r) => matchesRecipFilter(r, filter));
  const donut = [
    { key: 'read', label: 'Read', value: read, stroke: '#22c55e' },
    { key: 'unread', label: 'Opened inbox, unread', value: Math.max(delivered - read, 0), stroke: '#f59e0b' },
    { key: 'unopened', label: 'Not opened yet', value: Math.max(total - delivered, 0), stroke: '#ef4444' },
  ];
  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE));
  const cur = Math.min(page, totalPages);
  const pageRows = shown.slice((cur - 1) * PAGE, cur * PAGE);

  return (
    <div className="p-6">
      <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to messages
      </button>
      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {data && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="flex flex-wrap items-center gap-2 text-xl font-bold text-gray-900"><DeptBadge department={data.department} /> {data.subject}</h1>
              {/* The audit answer to "who issued this": taken from the sender's
                  sign-in when it was sent, not from anything typed. */}
              <p className="mt-1 text-xs text-gray-500">
                Created by <span className="font-medium text-gray-800">{data.sender_name || '—'}</span>
                {data.sender_email && <span className="text-gray-400"> ({data.sender_email})</span>}
                {' · '}{fmtDate(data.created_at)}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">{data.audience_summary}{data.edited_at ? ` · last edited ${fmtDate(data.edited_at)}${data.edited_by ? ` by ${data.edited_by}` : ''}` : ''}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {status !== 'cancelled' && (
                <button type="button" onClick={() => setEditOpen(true)}
                  className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"><PencilSquareIcon className="h-3.5 w-3.5" /> Edit</button>
              )}
              {isSent && (
                <>
                  <button type="button" onClick={() => exportAnalyticsCsv(data)}
                    className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"><ArrowDownTrayIcon className="h-3.5 w-3.5" /> CSV</button>
                  <button type="button" onClick={downloadPdf} disabled={pdfBusy}
                    className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"><DocumentArrowDownIcon className="h-3.5 w-3.5" /> {pdfBusy ? 'Preparing…' : 'PDF'}</button>
                  <button type="button" onClick={() => setAddOpen(true)}
                    className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"><UserPlusIcon className="h-3.5 w-3.5" /> Add recipients</button>
                  {newMembers?.newCount > 0 && (
                    <button type="button" onClick={() => addNew.mutate(id)} disabled={addNew.isPending}
                      className="flex items-center gap-1.5 rounded-md border border-emerald-500 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"><UserPlusIcon className="h-3.5 w-3.5" /> {addNew.isPending ? 'Adding…' : `Add ${newMembers.newCount} new member${newMembers.newCount === 1 ? '' : 's'}`}</button>
                  )}
                  <button type="button" onClick={() => remind.mutate({ id })} disabled={remind.isPending}
                    className="flex items-center gap-1.5 rounded-md border border-brand px-3 py-1.5 text-xs font-medium text-brand hover:bg-brand-light/40 disabled:opacity-50"><BellAlertIcon className="h-3.5 w-3.5" /> {remind.isPending ? 'Chasing…' : 'Chase all unread'}</button>
                </>
              )}
            </div>
          </div>

          {status === 'scheduled' && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <ClockIcon className="h-5 w-5 shrink-0" />
              <span className="min-w-0 flex-1">
                Scheduled to go out <span className="font-semibold">{fmtWhen(data.scheduled_for)}</span>. Nobody has it yet; the recipient list is worked out at that moment
                {data.recipient_count ? ` (about ${data.recipient_count} today)` : ''}.
              </span>
              <button type="button" disabled={release.isPending || cancel.isPending}
                onClick={() => { if (window.confirm('Send this message to everyone now instead of at its scheduled time?')) release.mutate(id); }}
                className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-dark disabled:opacity-50">{release.isPending ? 'Sending…' : 'Send now'}</button>
              <button type="button" disabled={release.isPending || cancel.isPending}
                onClick={() => { if (window.confirm('Cancel this scheduled message? It will not be sent.')) cancel.mutate(id); }}
                className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50">{cancel.isPending ? 'Cancelling…' : 'Cancel it'}</button>
              {(release.isError || cancel.isError) && (
                <p className="w-full text-xs text-red-600">{(release.error || cancel.error)?.response?.data?.error || 'That did not work — refresh and try again.'}</p>
              )}
            </div>
          )}
          {status === 'cancelled' && (
            <p className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              Cancelled {fmtDate(data.cancelled_at)}{data.cancelled_by ? ` by ${data.cancelled_by}` : ''} before it was sent — nobody received it.
            </p>
          )}
          {data.due_at && status !== 'cancelled' && (
            <p className={`mt-2 flex flex-wrap items-center gap-x-2 text-xs ${isSent && new Date(data.due_at) < new Date() ? 'text-red-600' : 'text-gray-600'}`}>
              <CalendarDaysIcon className="h-4 w-4" />
              <span>Complete by <span className="font-semibold">{fmtWhen(data.due_at)}</span>{isSent && new Date(data.due_at) < new Date() ? ' — deadline passed' : ''}</span>
              <span className="text-gray-400">
                · automatic reminders {data.auto_remind ? 'on' : 'off'}
                {(data.auto_reminders || []).filter((r) => r.reminded > 0).map((r) => ` · ${STAGE_LABEL[r.stage] || r.stage}: reminded ${r.reminded}`).join('')}
              </span>
            </p>
          )}
          {ackedUnwatched > 0 && (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
              {ackedUnwatched} {ackedUnwatched === 1 ? 'person has' : 'people have'} acknowledged but not yet watched the video in full.
            </p>
          )}
          {remind.data && <p className="mt-1 text-xs text-emerald-600">Reminded {remind.data.reminded} · {remind.data.emailed} emailed.</p>}
          {addNew.data && <p className="mt-1 text-xs text-emerald-600">Added {addNew.data.added} new recipient{addNew.data.added === 1 ? '' : 's'} · {addNew.data.emailed} emailed.</p>}

          {/* Read/acknowledge analytics only exist once a message has gone out. */}
          {isSent && (<>
          {/* Donut + legend (left) · stat cards (right) — Course Insights layout */}
          <div className="mt-4 flex flex-col gap-4 lg:flex-row">
            <div className="flex items-center justify-center gap-6 rounded-lg border border-gray-200 bg-white p-4 lg:w-1/2">
              <DonutChart total={total} segments={donut} />
              <div className="w-44"><DonutLegend total={total} segments={donut} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:w-1/2">
              <StatCard label="Recipients" value={total} />
              <StatCard label={`Read (${pct(read, total)}%)`} value={read} />
              <StatCard label={data.requires_ack ? `Acknowledged (${pct(ack, total)}%)` : `Notified (${pct(notified, total)}%)`} value={data.requires_ack ? ack : notified} />
              <StatCard label="Reminders sent" value={reminders} />
            </div>
          </div>

          {/* Recipients — tick people to remind, or chase all unread above. */}
          <div className="mt-5 mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">
              Recipients{filter !== 'all' && ` — showing ${shown.length} of ${total}`}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-wrap items-center gap-1">
                {RECIP_FILTERS(!!data.requires_ack, hasVideo).map((f) => (
                  <button key={f.key} type="button"
                    onClick={() => { setFilter(f.key); setPage(1); }}
                    className={`rounded-md border px-2.5 py-1 text-xs font-medium ${filter === f.key ? 'border-brand bg-brand-light/40 text-brand' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => refetch()} disabled={isFetching}
                className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                <ArrowPathIcon className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} /> {isFetching ? 'Refreshing…' : 'Refresh'}
              </button>
              {selected.size > 0 && (
                <button type="button" disabled={remind.isPending}
                  onClick={() => remind.mutate({ id, recipientIds: [...selected] }, { onSuccess: () => setSelected(new Set()) })}
                  className="flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-dark disabled:opacity-50">
                  <BellAlertIcon className="h-3.5 w-3.5" /> {remind.isPending ? 'Reminding…' : `Remind selected (${selected.size})`}
                </button>
              )}
            </div>
          </div>
          {/* Green/red status lights table */}
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50">
                <tr className="divide-x divide-gray-200 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="w-10 px-3 py-3"></th>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-3 py-3 text-center">Notified</th>
                  {hasVideo && <th className="px-3 py-3 text-center">Watched</th>}
                  <th className="px-3 py-3 text-center">Seen inbox</th>
                  <th className="px-3 py-3 text-center">Read</th>
                  {!!data.requires_ack && <th className="px-3 py-3 text-center">Acknowledged</th>}
                  <th className="px-4 py-3">Read at</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => (
                  <tr key={r.id ?? i} className="divide-x divide-gray-100 border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 text-center">{r.email ? <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleRecip(r.id)} aria-label={`Select ${r.display_name || r.email}`} /> : null}</td>
                    <td className="px-4 py-2 font-medium text-gray-800">{r.display_name || r.email || '—'}</td>
                    <td className="px-3 py-2 text-center">{(() => { const st = notifyState(r, sendEmail, tracked); return <NotifyDot state={st} title={notifyTitle(st)} />; })()}</td>
                    {hasVideo && (
                      <td className="px-3 py-2 text-center">
                        <span
                          title={`${r.watched_pct || 0}% watched`}
                          className={`text-xs font-medium ${r.all_watched ? 'text-emerald-600' : r.acked_unwatched ? 'text-amber-600' : 'text-gray-500'}`}>
                          {r.watched_pct || 0}%
                        </span>
                        {!r.all_watched && r.acked_before_watching && (
                          <span
                            data-flag="acked-blind-flag"
                            title="Acknowledged BEFORE watching — they watched it later, but the acknowledgement was made without having watched"
                            className="ml-1 rounded bg-red-100 px-1 text-xs font-semibold text-red-700">!</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-center"><StatusDot ok={!!r.delivered_at} title={r.delivered_at ? 'Has opened their inbox' : 'Has not opened their inbox'} /></td>
                    <td className="px-3 py-2 text-center"><StatusDot ok={!!r.read_at} title={r.read_at ? 'Read' : 'Unread'} /></td>
                    {!!data.requires_ack && <td className="px-3 py-2 text-center"><StatusDot ok={!!r.acknowledged_at} title={r.acknowledged_at ? 'Acknowledged' : 'Not acknowledged'} /></td>}
                    <td className="px-4 py-2 text-gray-500">{r.read_at ? fmtDate(r.read_at) : '—'}</td>
                  </tr>
                ))}
                {shown.length === 0 && <tr><td colSpan={(data.requires_ack ? 7 : 6) + (hasVideo ? 1 : 0)} className="px-4 py-6 text-center text-sm text-gray-400">{total === 0 ? 'No recipients.' : 'No recipients match this filter.'}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1"><StatusDot ok /> Done</span>
              <span className="flex items-center gap-1"><StatusDot ok={false} /> Not yet</span>
            </span>
            {totalPages > 1 && (
              <span className="flex items-center gap-2">
                <button type="button" disabled={cur === 1} onClick={() => setPage(cur - 1)} className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Prev</button>
                Page {cur} of {totalPages}
                <button type="button" disabled={cur === totalPages} onClick={() => setPage(cur + 1)} className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Next</button>
              </span>
            )}
          </div>
          </>)}

          {addOpen && (
            <AddRecipientsModal
              commId={id}
              existingOids={recips.map((r) => (r.entra_object_id || '').toLowerCase())}
              defaultSendEmail={!!data.send_email}
              onClose={() => setAddOpen(false)}
            />
          )}
          {editOpen && <EditMessageModal comm={data} onClose={() => setEditOpen(false)} />}
        </>
      )}
    </div>
  );
}

// Edit an already-sent message. Content is the single source of truth (the email
// is a notification with no content), so a correction shows everywhere it's read.
// Recipients + read/ack are preserved; the re-ack toggle clears acknowledgements
// so the ack-gate makes people confirm the corrected version.
function EditMessageModal({ comm, onClose }) {
  const update = useUpdateComm();
  const key = draftKey(`edit.${comm.id}`);
  // A draft is only picked up against the version it was started from; if the
  // message has been edited since (by anyone), the stale draft is dropped.
  const base = String(comm.edited_at || comm.created_at || '');
  const [draft, setDraft] = useState(() => { const d = readDraft(key); return d && d.base === base ? d : null; });
  const original = useMemo(() => ({
    department: comm.department || '',
    subject: comm.subject || '',
    blocks: (() => {
      if (comm.format === 'blocks') { try { const b = JSON.parse(comm.body); if (Array.isArray(b) && b.length) return b; } catch { /* fall through */ } }
      // Fixed id, so a refetch of the same message doesn't look like an edit.
      return [{ ...blankBlock('text'), id: 'b_original', text: comm.body || '' }];
    })(),
    priority: comm.priority === 'high' ? 'high' : 'normal',
    requiresAck: !!comm.requires_ack,
    scheduleAt: toLocalInput(comm.scheduled_for),
    dueAt: toLocalInput(comm.due_at),
    autoRemind: comm.auto_remind == null ? true : !!comm.auto_remind,
  }), [comm]);
  const isScheduled = comm.status === 'scheduled';
  const start = draft || original;
  const [department, setDepartment] = useState(start.department);
  const [subject, setSubject] = useState(start.subject);
  const [blocks, setBlocks] = useState(start.blocks);
  const [priority, setPriority] = useState(start.priority);
  const [requiresAck, setRequiresAck] = useState(start.requiresAck);
  const [scheduleAt, setScheduleAt] = useState(start.scheduleAt || '');
  const [dueAt, setDueAt] = useState(start.dueAt || '');
  const [autoRemind, setAutoRemind] = useState(start.autoRemind !== false);
  const [resetAcks, setResetAcks] = useState(false);
  const [error, setError] = useState('');
  const current = { department, subject, blocks, priority, requiresAck, scheduleAt, dueAt, autoRemind };
  // Opening a message and closing it again is not a draft.
  const changed = JSON.stringify(current) !== JSON.stringify(original);
  const draftSaved = useDraftAutosave(key, changed ? { base, ...current } : null, true);
  const discardDraft = () => {
    const o = original;
    clearDraft(key); setDraft(null);
    setDepartment(o.department); setSubject(o.subject); setBlocks(o.blocks); setPriority(o.priority); setRequiresAck(o.requiresAck);
    setScheduleAt(o.scheduleAt); setDueAt(o.dueAt); setAutoRemind(o.autoRemind);
  };

  const save = async () => {
    setError('');
    if (!subject.trim()) return setError('Add a subject.');
    const filled = blocks.filter(blockHasContent);
    if (!filled.length) return setError('Add some message content.');
    if (isScheduled && !scheduleAt) return setError('Choose when the message should go out, or use Send now.');
    if (isScheduled && scheduleAt !== original.scheduleAt && new Date(scheduleAt).getTime() < Date.now() + 60 * 1000) {
      return setError('Choose a release time in the future.');
    }
    // Only a deadline being set or moved is checked, as on the server.
    if (dueAt && dueAt !== original.dueAt
      && new Date(dueAt).getTime() <= (isScheduled ? new Date(scheduleAt).getTime() : Date.now())) {
      return setError(isScheduled ? 'The deadline must be after the release time.' : 'The deadline must be in the future.');
    }
    try {
      await update.mutateAsync({
        id: comm.id, department: department || null, subject, body: JSON.stringify(filled), format: 'blocks',
        media_ids: uploadedMediaIds(filled), priority, requires_ack: requiresAck, reset_acks: isScheduled ? false : resetAcks,
        ...(isScheduled ? { scheduled_for: fromLocalInput(scheduleAt) } : {}),
        due_at: fromLocalInput(dueAt),
        auto_remind: autoRemind,
      });
      clearDraft(key);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save changes.');
    }
    return undefined;
  };

  return (
    <Modal onClose={onClose} title="Edit message" full>
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
      <DraftNotice savedAt={draft?.savedAt} onDiscard={discardDraft} saveFailed={!draftSaved} />
      <div className="space-y-4">
        <DepartmentSelect value={department} onChange={setDepartment} allowNone />
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={255}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
        </div>
        <MessageBuilder blocks={blocks} onChange={setBlocks} livePreview />
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
          <label className="flex items-center gap-2"><input type="checkbox" checked={priority === 'high'} onChange={(e) => setPriority(e.target.checked ? 'high' : 'normal')} /> High priority</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} /> Require acknowledgement</label>
        </div>
        <TimingFields
          canSchedule={isScheduled} alreadyScheduled
          when="later" setWhen={() => {}} scheduleAt={scheduleAt} setScheduleAt={setScheduleAt}
          dueAt={dueAt} setDueAt={setDueAt} autoRemind={autoRemind} setAutoRemind={setAutoRemind}
          requiresAck={requiresAck}
        />
        {/* Nobody has acknowledged a message that hasn't gone out yet. */}
        {!isScheduled && (
          <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <input type="checkbox" checked={resetAcks} onChange={(e) => setResetAcks(e.target.checked)} className="mt-0.5" />
            <span>Require everyone to acknowledge again — clears existing acknowledgements so recipients must re-confirm the corrected message. Leave off for a minor fix.</span>
          </label>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={save} disabled={update.isPending}
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50">{update.isPending ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
      <LivePreview
        department={department} subject={subject} blocks={blocks} priority={priority} requiresAck={requiresAck}
        dueAt={dueAt} scheduleAt={isScheduled ? scheduleAt : ''} sentAt={isScheduled ? null : (comm.released_at || comm.created_at)}
        senderName={comm.sender_name}
      />
      </div>
    </Modal>
  );
}

// Pick more people to add to an already-sent message. Reuses the same target
// options as the composer; hides anyone who's already a recipient so it reads
// as "who's new/missing". Isolated from the composer's picker on purpose.
function AddRecipientsModal({ commId, existingOids, defaultSendEmail, onClose }) {
  const { data: opts } = useCommTargets();
  const add = useAddRecipients();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [sendEmail, setSendEmail] = useState(defaultSendEmail);
  const [done, setDone] = useState(null);

  const have = useMemo(() => new Set(existingOids || []), [existingOids]);
  const people = useMemo(() => (opts?.people || []).filter((p) => !have.has((p.entraId || '').toLowerCase())), [opts, have]);
  const departments = useMemo(() => [...new Set(people.map((p) => p.department).filter(Boolean))].sort(), [people]);

  const isEngineer = (p) => p.department && !/^office/i.test(p.department) && p.department !== 'Leaver';
  const matchesFilter = (p) => {
    if (filter === 'all') return true;
    if (filter === 'engineers') return isEngineer(p);
    const [t, v] = filter.split(/:(.+)/);
    if (t === 'dept') return p.department === v;
    if (t === 'region') return p.region === v;
    if (t === 'isp') return p.isp === v;
    if (t === 'cluster') return p.cluster === v;
    return true;
  };
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter((p) => matchesFilter(p) && (!q || (p.name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, filter, search]);
  useEffect(() => { setPage(0); }, [filter, search]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggle = (eid) => setSelected((s) => { const n = new Set(s); if (n.has(eid)) n.delete(eid); else n.add(eid); return n; });
  const selectAll = () => setSelected((s) => { const n = new Set(s); filtered.forEach((p) => n.add(p.entraId)); return n; });
  const clearAll = () => setSelected((s) => { const n = new Set(s); filtered.forEach((p) => n.delete(p.entraId)); return n; });

  const submit = async () => {
    if (!selected.size) return;
    const res = await add.mutateAsync({ id: commId, entraIds: [...selected], send_email: sendEmail });
    setDone(res);
  };

  if (done) {
    return (
      <Modal onClose={onClose} title="Recipients added">
        <div className="text-center">
          <UserPlusIcon className="mx-auto mb-3 h-10 w-10 text-brand" />
          <p className="text-sm text-gray-700">Added <span className="font-semibold">{done.added}</span> new recipient{done.added === 1 ? '' : 's'}{sendEmail ? ` · ${done.emailed} emailed` : ''}.</p>
          <button type="button" onClick={onClose} className="mt-5 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark">Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title="Add recipients" full>
      <p className="mb-3 text-xs text-gray-500">Only people who haven't already received this message are shown. They'll get it in their inbox{sendEmail ? ' and an email notification' : ''}.</p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
          <option value="all">All staff</option>
          <option value="engineers">Engineers</option>
          {departments.map((d) => <option key={d} value={`dept:${d}`}>{d}</option>)}
          {(opts?.regions || []).map((r) => <option key={`r${r}`} value={`region:${r}`}>Region: {r}</option>)}
          {(opts?.isps || []).map((r) => <option key={`i${r}`} value={`isp:${r}`}>ISP: {r}</option>)}
          {(opts?.clusters || []).map((r) => <option key={`c${r}`} value={`cluster:${r}`}>Cluster: {r}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…"
          className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
      </div>
      <div className="mb-2 flex items-center gap-3 text-xs">
        <button type="button" onClick={selectAll} className="font-medium text-brand hover:text-brand-dark">Select all ({filtered.length})</button>
        <button type="button" onClick={clearAll} className="text-gray-400 hover:text-gray-600">Clear these</button>
        <span className="ml-auto text-gray-500">{selected.size} selected</span>
      </div>
      <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <tbody className="divide-y divide-gray-100">
            {paged.map((p) => {
              const on = selected.has(p.entraId);
              return (
                <tr key={p.entraId} className={`cursor-pointer ${on ? 'bg-brand-light/40' : 'hover:bg-gray-50'}`} onClick={() => toggle(p.entraId)}>
                  <td className="w-10 px-3 py-2 text-center"><input type="checkbox" checked={on} readOnly /></td>
                  <td className="px-3 py-2 font-medium text-gray-800">{p.name || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{p.email || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-400">{p.department || ''}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td className="px-3 py-6 text-center text-sm text-gray-400">Nobody new matches.</td></tr>}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="mt-2 flex items-center justify-end gap-2 text-xs text-gray-500">
          <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Prev</button>
          Page {page + 1} of {pageCount}
          <button type="button" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)} className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Next</button>
        </div>
      )}
      <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} /> Send email notification to the added people
      </label>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="button" onClick={submit} disabled={!selected.size || add.isPending}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50">{add.isPending ? 'Adding…' : `Add ${selected.size || ''}`.trim()}</button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, wide, full }) {
  const size = full ? 'max-w-6xl' : wide ? 'max-w-2xl' : 'max-w-sm';
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className={`max-h-[92vh] w-full ${size} overflow-y-auto rounded-lg bg-white p-6 shadow-xl`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Dispatch list filters (06/10/2026). The whole list is already loaded, so
// filtering and paging happen here.
const STATUS_FILTERS = [
  { key: '', label: 'Any status' },
  { key: 'sent', label: 'Sent' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'ack-outstanding', label: 'Ack outstanding' },
];
const NO_FILTERS = { q: '', department: '', creator: '', status: '', from: '', to: '' };
const statusOf = (c) => c.status || 'sent';
const whenOf = (c) => new Date(c.status === 'scheduled' ? c.scheduled_for : (c.released_at || c.created_at));

function matchesListFilters(c, f) {
  const q = f.q.trim().toLowerCase();
  if (q && ![c.subject, c.sender_name, c.sender_email, c.audience_summary].some((v) => String(v || '').toLowerCase().includes(q))) return false;
  if (f.department && (c.department || GENERAL.key) !== f.department) return false;
  if (f.creator && (c.sender_name || c.sender_email || '') !== f.creator) return false;
  if (f.status === 'ack-outstanding') {
    if (!(statusOf(c) === 'sent' && c.requires_ack && Number(c.ack_count) < Number(c.recipient_count))) return false;
  } else if (f.status && statusOf(c) !== f.status) return false;
  // From/To are whole UK days (inputs give yyyy-mm-dd), inclusive.
  const when = whenOf(c);
  if (f.from && when < new Date(`${f.from}T00:00:00`)) return false;
  if (f.to && when > new Date(`${f.to}T23:59:59.999`)) return false;
  return true;
}

function CommsDispatch() {
  const navigate = useNavigate();
  const { data: comms, isLoading } = useComms();
  const [composing, setComposing] = useState(false);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [page, setPage] = useState(0);
  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(0); };
  const filtersOn = Object.values(filters).some(Boolean);

  const creators = useMemo(
    () => [...new Set((comms || []).map((c) => c.sender_name || c.sender_email).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [comms]
  );
  const departments = useMemo(
    () => [...COMMS_DEPARTMENTS, GENERAL].filter((d) => (comms || []).some((c) => (c.department || GENERAL.key) === d.key)),
    [comms]
  );
  const filtered = useMemo(() => (comms || []).filter((c) => matchesListFilters(c, filters)), [comms, filters]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paged = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const control = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  return (
    <div className="p-6">
      <button type="button" onClick={() => navigate('/?dept=communications')} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Communications
      </button>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><MegaphoneIcon className="h-7 w-7 text-brand" /> Comms Dispatch</h1>
        <button type="button" onClick={() => setComposing(true)}
          className="flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand-dark">
          <PaperAirplaneIcon className="h-4 w-4" /> New message
        </button>
      </div>
      <p className="mb-6 text-sm text-gray-500">Send communications to individuals, a Region/ISP/Cluster, a group, or everyone — and track who's read and acknowledged.</p>

      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {comms && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input value={filters.q} onChange={(e) => setFilter('q', e.target.value)} placeholder="Search subject, creator or audience…" className={`${control} w-full sm:w-64`} />
          <select value={filters.department} onChange={(e) => setFilter('department', e.target.value)} className={control}>
            <option value="">All departments</option>
            {departments.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <select value={filters.creator} onChange={(e) => setFilter('creator', e.target.value)} className={control}>
            <option value="">Anyone</option>
            {creators.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} className={control}>
            {STATUS_FILTERS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <label className="flex items-center gap-1 text-xs text-gray-500">
            From <input type="date" value={filters.from} onChange={(e) => setFilter('from', e.target.value)} className={control} />
          </label>
          <label className="flex items-center gap-1 text-xs text-gray-500">
            To <input type="date" value={filters.to} onChange={(e) => setFilter('to', e.target.value)} className={control} />
          </label>
          {filtersOn && (
            <button type="button" onClick={() => { setFilters(NO_FILTERS); setPage(0); }} className="text-sm font-medium text-brand hover:text-brand-dark">Clear filters</button>
          )}
          <span className="ml-auto text-xs text-gray-400">{filtered.length} of {comms.length} message{comms.length === 1 ? '' : 's'}</span>
        </div>
      )}
      {comms && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2.5">Department</th><th className="px-4 py-2.5">Subject</th><th className="px-4 py-2.5">Created by</th><th className="px-4 py-2.5">Audience</th>
                <th className="px-4 py-2.5 text-right">Recipients</th><th className="px-4 py-2.5">Read</th>
                <th className="px-4 py-2.5">Ack</th><th className="px-4 py-2.5">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {paged.map((c) => {
                const sent = !c.status || c.status === 'sent';
                return (
                  <tr key={c.id} className={`cursor-pointer hover:bg-brand-light/40 ${c.status === 'cancelled' ? 'opacity-60' : ''}`} onClick={() => navigate(`/comms/analytics/${c.id}`)}>
                    <td className="whitespace-nowrap px-4 py-2.5"><DeptBadge department={c.department} /></td>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-800">
                      {c.status === 'scheduled' && <span className="mr-1 inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800"><ClockIcon className="h-3 w-3" /> Scheduled</span>}
                      {c.status === 'cancelled' && <span className="mr-1 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold text-gray-600">Cancelled</span>}
                      {c.priority === 'high' && <span className="mr-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600">High</span>}
                      {c.subject}
                      {c.requires_ack ? <CheckBadgeIcon className="ml-1 inline h-4 w-4 text-emerald-500" title="Requires acknowledgement" /> : null}
                      {c.due_at && c.status !== 'cancelled' && (
                        <span className={`ml-1 whitespace-nowrap text-xs font-normal ${sent && new Date(c.due_at) < new Date() ? 'text-red-600' : 'text-gray-400'}`}>· due {fmtDate(c.due_at)}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700" title={c.sender_email || undefined}>{c.sender_name || c.sender_email || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{c.audience_summary}</td>
                    {/* Before release the count is today's estimate; the list is resolved when it goes. */}
                    <td className="px-4 py-2.5 text-right text-sm text-gray-600">{sent ? c.recipient_count : <span className="text-gray-400" title="Estimated — worked out when it is sent">~{c.recipient_count}</span>}</td>
                    <td className="px-4 py-2.5">{sent ? <Bar value={c.read_count} total={c.recipient_count} /> : <span className="text-xs text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5">{sent && c.requires_ack ? <Bar value={c.ack_count} total={c.recipient_count} tone="green" /> : <span className="text-xs text-gray-300">—</span>}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500">
                      {c.status === 'scheduled' ? <span className="text-amber-800">Goes {fmtDate(c.scheduled_for)}</span>
                        : c.status === 'cancelled' ? '—'
                          : fmtDate(c.released_at || c.created_at)}
                    </td>
                  </tr>
                );
              })}
              {comms.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-400">No messages sent yet.</td></tr>}
              {comms.length > 0 && filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-400">No messages match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {filtered.length > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-end gap-3 text-sm">
          <button type="button" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}
            className="rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">Previous</button>
          <span className="text-gray-500">Page {currentPage + 1} of {pageCount}</span>
          <button type="button" disabled={currentPage >= pageCount - 1} onClick={() => setPage(currentPage + 1)}
            className="rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">Next</button>
        </div>
      )}

      {composing && <Composer onClose={() => setComposing(false)} />}
    </div>
  );
}

export default CommsDispatch;
